// src/hooks/useDashboardFilterPersistence.ts
/**
 * Per-dashboard filter persistence, plus session-only search text.
 *
 * Each dashboard tab stores its own filters independently in localStorage
 * under the key C2_Filters_Dashboard_{dashboardId}. When dashboardId changes
 * (user switches tab), the hook re-reads the matching entry from localStorage
 * so the previous selections are immediately restored.
 *
 * SearchText is intentionally NOT persisted: it resets to '' on every
 * dashboard selection (passed in via `resetKey`, which the caller bumps on
 * every tab click — including reclicking the already-active tab) and on
 * page refresh.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { FilterOption } from '@/types/dashboard';

function loadFilters(dashboardId: number | null): FilterOption[]
{
  if (!dashboardId) return [];
  try
  {
    const raw = localStorage.getItem(`C2_Filters_Dashboard_${dashboardId}`);
    if (raw)
    {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  }
  catch
  {
    // ignore
  }
  return [];
}

// Convert a FilterOption into the API filter format expected by DashboardGroups / other views
export const convertFilterToApi = (opt: FilterOption) => ({
  Name: opt.FieldName,
  IsCustomField: opt.IsCustomField,
  FieldTypeId: opt.FieldTypeId,
  CustomFields: opt.CustomFields || null,
  ComparisonOperator: opt.ComparisonOperator || null,
  isChipFilter: null,
  isSelected: true,
  Text: opt.Name,
  Value: opt.Id,
  isQuickFilter: false,
});

export const useDashboardFilterPersistence = (
  dashboardId: number | null,
  resetKey: number = 0,
) =>
{
  const [currentId, setCurrentId] = useState(dashboardId);
  const [filtersSelected, setFiltersSelected] = useState<FilterOption[]>(() =>
    loadFilters(dashboardId),
  );
  const [appliedFilters, setAppliedFilters] = useState<FilterOption[]>(() =>
    loadFilters(dashboardId),
  );
  const [searchText, setSearchTextState] = useState<string>('');

  // Synchronously update state during render if dashboardId changes
  // This avoids passing stale filters momentarily from the previous tab
  if (dashboardId !== currentId)
  {
    setCurrentId(dashboardId);
    const savedFilters = loadFilters(dashboardId);
    setFiltersSelected(savedFilters);
    setAppliedFilters(savedFilters);
  }

  // Search text is never persisted — clear it every time the caller signals
  // a dashboard selection (resetKey bumps on every tab click, even a reclick
  // of the already-active tab, so this fires in that case too).
  const [currentResetKey, setCurrentResetKey] = useState(resetKey);
  if (resetKey !== currentResetKey)
  {
    setCurrentResetKey(resetKey);
    setSearchTextState('');
  }

  // Ref to always read the latest filtersSelected inside callbacks
  const filtersSelectedRef = useRef(filtersSelected);
  filtersSelectedRef.current = filtersSelected;

  // Persist applied filters whenever they change.
  // Note: Since we rely on appliedFilters directly, this will also fire
  // right after a tab switch (saving the same data back), which is harmless.
  useEffect(() =>
  {
    if (!dashboardId) return;
    localStorage.setItem(`C2_Filters_Dashboard_${dashboardId}`, JSON.stringify(appliedFilters));
  }, [dashboardId, appliedFilters]);

  const handleFilterSelect = useCallback((option: FilterOption) =>
  {
    setFiltersSelected(prev =>
    {
      if (prev.some(f => f.Id === option.Id && f.FieldName === option.FieldName)) return prev;
      return [...prev, option];
    });
  }, []);

  const handleFilterUnselect = useCallback((option: FilterOption) =>
  {
    setFiltersSelected(prev =>
      prev.filter(f => !(f.Id === option.Id && f.FieldName === option.FieldName)),
    );
  }, []);

  /** Called when the "Filter" button is clicked — commits filtersSelected -> appliedFilters */
  const handleApplyFilters = useCallback(() =>
  {
    setAppliedFilters(filtersSelectedRef.current);
  }, []);

  const setSearchText = useCallback((text: string) =>
  {
    setSearchTextState(text);
  }, []);

  // Convert appliedFilters to the JSON string expected by DashboardGroups / CalendarView etc.
  const filtersJson = useMemo(
    () => JSON.stringify(appliedFilters.map(convertFilterToApi)),
    [appliedFilters],
  );

  // Extra top-level params derived from all applied filters.
  // Standard (non-custom) filters are flattened to top-level XML properties so the
  // stored procedure can read them (Filters is [XmlIgnore] and never reaches the SP XML).
  const extraFilterParams = useMemo(() =>
  {
    const extra: Record<string, any> = {};
    const customFields: any[] = [];

    appliedFilters.forEach(f =>
    {
      if (f.IsCustomField)
      {
        customFields.push({
          Name: f.FieldName,
          FieldTypeId: f.FieldTypeId,
          Value: f.Id,
          ComparisonOperator: f.ComparisonOperator || null,
        });
      }
      else
      {
        const num = parseInt(String(f.Id), 10);
        extra[f.FieldName] = isNaN(num) ? f.Id : num;
      }
    });

    if (customFields.length > 0)
    {
      extra.CustomFields = customFields;
    }

    extra.IsFilterPersonal = false;
    return extra;
  }, [appliedFilters]);

  return {
    // UI chip state
    filtersSelected,
    // Handler wired to FilterBar
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
    // Search text (session-only, resets on dashboard selection/refresh)
    searchText,
    setSearchText,
    // Ready-to-use values for child components
    filtersJson, // JSON string for DashboardGroups `filters` prop
    appliedFilters, // Raw array (if needed)
    extraFilterParams, // Top-level API params for toggle filters
  };
};
